/**
 * App Store rating prompt.
 *
 * Uses Apple's native StoreKit review prompt via the Despia bridge
 * (`requestreview://`). Apple shows this dialog at most 3 times per 365 days
 * per device and ignores extra calls — it's safe to invoke at good moments.
 *
 * Apple guideline: never trigger from a button tap; trigger after the user
 * just completed something positive (a workout, hitting a streak, etc.) and
 * wait a beat so it doesn't interrupt them.
 */

import { fireAndForget, isNativeApp } from "./safeDespia";

const STORAGE_KEY = "spotrai_app_rating_state_v1";
const FIRST_SEEN_KEY = "spotrai_first_seen_at";

// Don't ask before the user has used the app a bit.
const MIN_WORKOUTS_BEFORE_PROMPT = 2;
const MIN_HOURS_AFTER_FIRST_LAUNCH = 24;
// We let Apple enforce its own cap, but we additionally avoid spamming the
// same scheme more than once every 60 days from our side.
const MIN_DAYS_BETWEEN_PROMPTS = 60;

interface RatingState {
  workoutsCompleted: number;
  lastPromptAt: number | null;
  promptCount: number;
}

function readState(): RatingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { workoutsCompleted: 0, lastPromptAt: null, promptCount: 0, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { workoutsCompleted: 0, lastPromptAt: null, promptCount: 0 };
}

function writeState(state: RatingState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function getFirstSeenAt(): number {
  try {
    const v = localStorage.getItem(FIRST_SEEN_KEY);
    if (v) return Number(v) || Date.now();
    const now = Date.now();
    localStorage.setItem(FIRST_SEEN_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

function triggerNativePrompt() {
  // Despia exposes Apple's SKStoreReviewController via this scheme.
  // No-op on the web — `fireAndForget` already short-circuits when not native.
  fireAndForget("requestreview://");
}

/**
 * Call this after a positive completion event (e.g. a workout finished).
 * Returns true if a prompt was actually requested.
 */
export function maybePromptForRating(reason: "workout-completed" | "manual" = "workout-completed"): boolean {
  if (!isNativeApp()) return false;

  const state = readState();
  const firstSeenAt = getFirstSeenAt();
  const hoursSinceFirstLaunch = (Date.now() - firstSeenAt) / (1000 * 60 * 60);

  if (reason === "workout-completed") {
    state.workoutsCompleted += 1;
    writeState(state);
  }

  if (state.workoutsCompleted < MIN_WORKOUTS_BEFORE_PROMPT) return false;
  if (hoursSinceFirstLaunch < MIN_HOURS_AFTER_FIRST_LAUNCH) return false;

  if (state.lastPromptAt) {
    const daysSince = (Date.now() - state.lastPromptAt) / (1000 * 60 * 60 * 24);
    if (daysSince < MIN_DAYS_BETWEEN_PROMPTS) return false;
  }

  // Delay a touch so we don't interrupt the success animation / save.
  setTimeout(() => {
    triggerNativePrompt();
    writeState({ ...state, lastPromptAt: Date.now(), promptCount: state.promptCount + 1 });
  }, 1500);

  return true;
}
